<template>
  <div class="mt-5">
    <div class="row px-0 mb-4">
      <div class="col-2">
        <div class="b-form__container">
          <checkbox class="b-checkbox" :id="`${isAmortizable}${assetId}${assetComponentIndex}`" v-model="isAmortizable" @change="uncheckIsAmortizable()">{{$t('CONTENT.assets.isAmortizable')}}</checkbox>
        </div>
      </div>
      <div class="col-2" v-if="isAmortizable">
        <div class="b-form__container" data-cy="asset-component-liquidationValue">
          <label>
            {{$t('CONTENT.assets.liquidation_value')}}:
            <span class="b-form__required">*</span>
          </label>
          <numeric v-bind:class="{ 'b-numeric__error': isFieldWithError('liquidationValue', liquidationValue) }"
            class="b-numeric" controls :min="0" :max="1000000000000" v-model="liquidationValue"></numeric>
        </div>
      </div>
    </div>
    <div class="row mb-4 px-0" v-if="isSeveralAssetComponents">
      <div class="col-2">
        <div class="b-form__container">
          <label for="name">{{$t('CONTENT.assets.asset_component_name')}}:</label>
          <input
            data-cy="asset-component-name"
            type="text"
            id="name"
            class="b-input"
            name="name"
            v-model="name"
          />
        </div>
      </div>
      <div class="col-2">
        <div class="b-form__container">
          <label for="externalId">{{$t('CONTENT.external_id')}}:</label>
          <input
            data-cy="asset-component-externalId"
            type="text"
            id="externalId"
            class="b-input"
            name="externalId"
            v-model="externalId"
          />
        </div>
      </div>
    </div>
    <div class="row px-0">
      <div class="col-2">
        <div class="b-form__container" data-cy="asset-component-quantity">
          <label>
            {{$t('CONTENT.assets.leased_amount')}}:
            <span class="b-form__required">*</span>
          </label>
          <numeric v-bind:class="{ 'b-numeric__error': isFieldWithError('quantity', quantity) }" class="b-numeric" controls :min="0" :max="1000000000000" v-model="quantity"></numeric>
        </div>
      </div>
      <div class="col-2">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.unitOfMeasure')}}:
            <span class="b-form__required">*</span>
          </label>
          <cool-select :list="unitsOfMeasure"
                       option-value="code"
                       option-text="name"
                       :placeholder="$t('CONTENT.choose')"
                       :isError="isFieldWithError('unitOfMeasure', unitOfMeasure)"
                       v-model="unitOfMeasure"></cool-select>
        </div>
      </div>
    </div>
    <div class="row mt-5 px-0 b-form__row_type_flex b-form__row_type_flex-end">
      <div class="col-2">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.lease_start_date')}}:
            <span class="b-form__required">*</span>
          </label>
          <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('startDate', startDate) }" class="b-datepicker" v-model="startDate" :formatter="datepickerFormat" :disabled-date="maxDateForStartDateFn"></datepicker>
        </div>
      </div>
      <div class="col-2">
        <div class="b-form__container">
          <label>
            {{$t('CONTENT.assets.lease_end_date')}}:
            <span class="b-form__required">*</span>
          </label>
          <datepicker  v-bind:class="{ 'b-datepicker__error': isFieldWithError('endDate', endDate) }" class="b-datepicker" v-model="endDate" :formatter="datepickerFormat" :disabled-date="minDateForEndDateFn"></datepicker>
        </div>
      </div>
      <div class="col-2 b-form__container_type_flex">
        <checkbox class="b-checkbox" :id="`${isPermanent}${assetId}${assetComponentIndex}`" v-model="isPermanent">{{$t('CONTENT.assets.perpetual')}}</checkbox>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.isPermanentHint')" icon="question"></dynamic-icon>
      </div>
    </div>
    <div class="row mt-5">
      <div class="col-4">
        <div class="b-form__container">
          <label for="externalId">{{$t('CONTENT.assets.end_date_commentary')}}:</label>
          <textarea
            :data-cy="`endDateCommentary-${assetId}-${assetComponentIndex}`"
            type="text"
            :id="`endDateCommentary-${assetId}-${assetComponentIndex}`"
            class="b-textarea"
            name="`endDateCommentary-${assetComponentIndex}`"
            v-model="endDateCommentary"
          />
        </div>
      </div>
    </div>
    <div class="mt-6">
      <h4 class="mb-2">{{$t('CONTENT.assets.options')}}</h4>
      <ExtensionOption :currentAssetComponent="currentAssetComponent" :currentAssetComponentIndex="assetComponentIndex" @changeExtension="onChangeData"></ExtensionOption>
      <TerminationOption :currentAssetComponent="currentAssetComponent" :currentAssetComponentIndex="assetComponentIndex" :currencyCodes="currencyCodes" @changeTermination="onChangeData"></TerminationOption>
      <BuyoutOption :currentAssetComponent="currentAssetComponent" :currentAssetComponentIndex="assetComponentIndex" :currencyCodes="currencyCodes" @changeBuyout="onChangeData"></BuyoutOption>
    </div>
    <div class="mt-6 b-contracts__component-wrap">
      <div class="mb-2 b-form__container_type_flex">
        <h4>{{$t('CONTENT.assets.devaluations')}}</h4>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.devaluationHint')" icon="question"></dynamic-icon>
      </div>
      <div v-for="(item, index) in devaluations" :key="index" class="b-contracts__component-item">
        <DevaluationsItem
          :devaluationsItem="item"
          :devaluationsItemIndex="index"
          :devaluationsLength="devaluations.length"
          :assetComponent="assetComponent"
          @onDeleteDevaulation="onDeleteDevaulation"
        ></DevaluationsItem>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-3">
        <div class="b-form__container">
          <button
            type="button"
            class="b-btn"
            @click="addDevaluations"
            :disabled="isDisabledAddDevaluations"
          >
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
            {{$t('CONTENT.assets.add_devaluation')}}
          </button>
        </div>
      </div>
    </div>
    <div class="mt-6 b-contracts__component-wrap">
      <div class="mb-2 b-form__container_type_flex">
        <h4>{{$t('CONTENT.assets.expenses')}}</h4>
        <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.expensesHint')" icon="question"></dynamic-icon>
      </div>
      <div v-for="(item, index) in expenses" :key="index" class="b-contracts__component-item">
        <ExpensesItem
          :expensesItem="item"
          :expensesItemIndex="index"
          :expensesLength="expenses.length"
          :assetComponent="assetComponent"
          @onDeleteExpenses="onDeleteExpenses"
        ></ExpensesItem>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-3">
        <div class="b-form__container">
          <button
            type="button"
            class="b-btn"
            @click="addExpenses"
            :disabled="isDisabledAddExpenses"
          >
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
            {{$t('CONTENT.assets.add_expenses')}}
          </button>
        </div>
      </div>
    </div>
    <div class="row mt-8 py-4 px-6 b-asset__bottom-panel">
      <div class="b-asset__bottom-panel-item-wrap">
        <div class="b-asset__bottom-panel-item mr-10">
          <div class="b-asset__bottom-panel-item-name mr-2">{{$t('CONTENT.assets.result_rent_date')}}:</div>
          <div
            class="b-asset__bottom-panel-item-value"
            data-cy="asset-component-result-date"
            v-if="startDateForPanel !== null && actualEndDate !== null"
          >{{startDateForPanel}} - {{actualEndDate | dateFormatted}}
          </div>
        </div>
        <div class="b-asset__bottom-panel-item mr-10">
          <div class="b-asset__bottom-panel-item-name mr-2">{{$t('CONTENT.assets.option_month_count')}}:</div>
          <div class="b-asset__bottom-panel-item-value">
            <MonthCalculatorWidget :startDate="startDateForCalc" :endDate="actualEndDate"/>
          </div>
        </div>
      </div>
      <div class="b-asset__bottom-panel-btn" v-if="assetComponentsLength > 1">
        <button type="button" class="b-btn" @click="deleteAssetComponent()">
          <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="delete"></dynamic-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import dateConverter from "../../../utils/converters/date.converter";
  import MonthCalculatorWidget from "../../../components/widgets/MonthCalculatorWidget";
  import TerminationOption from "../../../components/registry/contracts/create/assets/TerminationOption";
  import ExtensionOption from "../../../components/registry/contracts/create/assets/ExtensionOption";
  import BuyoutOption from "../../../components/registry/contracts/create/assets/BuyoutOption";
  import DateWithoutTimeAPI from "../../../api/DateWithoutTimeAPI";
  import DevaluationsItem from "./DevaluationsItem";
  import ExpensesItem from "./ExpensesItem";
  import datepickerFormat from '@/mixins/datepickerFormat.mixin';


	export default {
	  components: {
      MonthCalculatorWidget,
      TerminationOption,
      ExtensionOption,
      BuyoutOption,
      DevaluationsItem,
      ExpensesItem,
    },
    mixins: [datepickerFormat],
	  props: {
      assetComponent: {
        required: true,
      },
      isSeveralAssetComponents: {
        type: Boolean,
        required: true,
      },
      unitsOfMeasure: {
        type: Array,
        required: true,
      },
      assetId: {
        required: true,
      },
      assetComponentIndex: {
        type: Number,
        required: true,
      },
      currencyCodes: {
        type: Array,
        required: true,
      },
      assetComponentsLength: {
        type: Number,
        required: true,
      },
      validationErrors: {
        type: Array
      }
    },
    data() {
      return {
        startDateForPanel: null,
        endDateForPanel: null,
        startDateForCalc: null,
        endDateForCalc: null,
        maxDateForStartDate: null,
        minDateForEndDate: null,
        devaluations: [],
        expenses: [],
      }
    },
    created() {
      if (this.assetComponent.devaluations.length !== 0) {
        this.devaluations = this.assetComponent.devaluations;
      }
      if (this.assetComponent.expenses.length !== 0) {
        this.expenses = this.assetComponent.expenses;
      }
    },
    watch: {
      isSeveralAssetComponents(val) {
        if(val === false) {
          this.name = null;
          this.externalId = null;
        }
      },
    },
    computed: {
      isDisabledAddDevaluations() {
        for (let i = 0; i < this.devaluations.length; i++) {
          if (
            this.devaluations[i].startDate === null ||
            this.devaluations[i].sum === null
          ) {
            return true;
          }
        }

        return false;
      },
      isDisabledAddExpenses() {
        for (let i = 0; i < this.expenses.length; i++) {
          if (
            this.expenses[i].startDate === null ||
            this.expenses[i].sum === null
          ) {
            return true;
          }
        }

        return false;
      },
	    currentAssetComponent() {
	      return this.assetComponent;
      },
      name: {
        get() {
          if(this.currentAssetComponent.name !== null) {
            return this.currentAssetComponent.name;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "name",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      externalId: {
        get() {
          if(this.currentAssetComponent.externalId !== null) {
            return this.currentAssetComponent.externalId;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "externalId",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      quantity: {
        get() {
          if (this.currentAssetComponent.quantity !== null) {
            return this.currentAssetComponent.quantity;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "quantity",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      liquidationValue: {
        get() {
          if (this.currentAssetComponent.liquidationValue !== null) {
            return this.currentAssetComponent.liquidationValue;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "liquidationValue",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      unitOfMeasure: {
        get() {
          if(this.currentAssetComponent.unitOfMeasureCode !== null) {
            return this.currentAssetComponent.unitOfMeasureCode;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "unitOfMeasureCode",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      startDate: {
        get() {
          if (this.currentAssetComponent.startDate === null) {
            return null;
          } else {
            return dateConverter.convertToDate(this.currentAssetComponent.startDate);
          }
        }, set(newVal) {
          if (newVal !== null) {
            this.startDateForPanel = this.$d(newVal);
            this.startDateForCalc = newVal;
            this.minDateForEndDate = newVal;
            let startDate = dateConverter.convertToString(newVal);
            this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
              name: "startDate",
              value: startDate,
              assetComponent: this.assetComponent,
            });
            this.onChangeData();
            if (
              this.currentAssetComponent.startDate !== null &&
              this.currentAssetComponent.startDate !== "" &&
              this.isPermanent
            ) {
              this.getDateWithPermanent();
            }
          } else {
            this.currentAssetComponent.startDate = null;
          }
        }
      },
      endDate: {
        get() {
          if (this.currentAssetComponent.endDate === null) {
            return null;
          } else {
            return dateConverter.convertToDate(this.currentAssetComponent.endDate);
          }
        }, set(newVal) {
          if(newVal !== null) {
            this.endDateForPanel = this.$d(newVal);
            this.endDateForCalc = newVal;
            this.maxDateForStartDate = newVal;
            let endDate = dateConverter.convertToString(newVal);
            this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
              name: "endDate",
              value: endDate,
              assetComponent: this.assetComponent,
            });
            this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
              name: "endDateInitial",
              value: endDate,
              assetComponent: this.assetComponent,
            });
            this.onChangeData();
            if (
              this.currentAssetComponent.startDate !== null &&
              this.currentAssetComponent.startDate !== "" &&
              this.isPermanent
            ) {
              this.getDateWithPermanent();
            }
          } else {
            this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
              name: "endDate",
              value: null,
              assetComponent: this.assetComponent,
            });
            this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
              name: "endDateInitial",
              value: null,
              assetComponent: this.assetComponent,
            });
          }
        }
      },
      isDisabledEndDate() {
        return !!this.currentAssetComponent.isPermanent;
      },
      isAmortizable: {
        get() {
          if (this.currentAssetComponent.isAmortizable !== null) {
            return this.currentAssetComponent.isAmortizable;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "isAmortizable",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      isPermanent: {
        get() {
          if (this.currentAssetComponent.isPermanent !== null) {
            return this.currentAssetComponent.isPermanent;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "isPermanent",
            value: newVal,
            assetComponent: this.assetComponent,
          });

          if (
            this.currentAssetComponent.startDate !== null &&
            this.currentAssetComponent.startDate !== "" &&
            newVal
          ) {
            this.getDateWithPermanent();
          }

          this.onChangeData();
        }
      },
      endDateCommentary: {
        get() {
          if(this.currentAssetComponent.endDateCommentary !== null) {
            return this.currentAssetComponent.endDateCommentary;
          } else {
            return null;
          }
        },
        set(newVal) {
          this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
            name: "endDateCommentary",
            value: newVal,
            assetComponent: this.assetComponent,
          });
          this.onChangeData();
        }
      },
      actualEndDate() {
        if (
          this.currentAssetComponent.extension != null &&
          this.currentAssetComponent.extension.date != null
        )
          return dateConverter.convertToDate(
            this.currentAssetComponent.extension.date
          );
        if (
          this.currentAssetComponent.termination != null &&
          this.currentAssetComponent.termination.date != null
        )
          return dateConverter.convertToDate(
            this.currentAssetComponent.termination.date
          );
        return this.endDateForCalc;
      },
    },
    methods: {
      uncheckIsAmortizable() {
        if(this.isAmortizable === false) {
          this.liquidationValue = 0;
        }
      },

      minDateForEndDateFn(date) {
        if(this.minDateForEndDate !== null) {
          return date < this.minDateForEndDate;
        }
      },
      maxDateForStartDateFn(date) {
        if(this.maxDateForStartDate !== null) {
          return date > this.maxDateForStartDate;
        }
      },
      deleteAssetComponent() {
        this.$emit('deleteAssetComponent', this.assetComponent);
        this.$eventBus.$emit('deleteAssetComponent', this.assetComponentIndex);
        this.onChangeData();
      },
      async getDateWithPermanent() {
        const convertedCurrentAssetComponentStartDate = encodeURIComponent(
          this.currentAssetComponent.startDate
        );
        let newDate = await new DateWithoutTimeAPI().addYearsBasedOnPerpetuity(
          convertedCurrentAssetComponentStartDate
        );
        this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
          name: "endDate",
          value: newDate,
          assetComponent: this.assetComponent,
        });
        this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
          name: "endDateInitial",
          value: newDate,
          assetComponent: this.assetComponent,
        });
        this.onChangeData();
      },
      onChangeData() {
        this.$emit('changeAssetComponentData');
      },
      addDevaluations() {
        let item = {
          id: null,
          sum: null,
          startDate: null
        };

        this.devaluations.push(item);

        this.$store.commit("assetComponentsPlugin/addAssetComponentNewDevaluation", {
          devaluationsItem: item,
          assetComponent: this.assetComponent,
        });
      },
      addExpenses() {
        let item = {
          id: null,
          type: null,
          sum: null
        };

        this.expenses.push(item);

        this.$store.commit("assetComponentsPlugin/addAssetComponentNewExpenses", {
          expensesItem: item,
          assetComponent: this.assetComponent,
        });
      },
      onDeleteDevaulation(itemIndex, item) {
        this.devaluations.splice(itemIndex, 1);

        this.$store.commit("assetComponentsPlugin/removeAssetComponentDevaluation", {
          devaluationsItem: item,
          assetComponent: this.assetComponent,
        });
      },
      onDeleteExpenses(itemIndex, item) {
        this.expenses.splice(itemIndex, 1);

        this.$store.commit("assetComponentsPlugin/removeAssetComponentExpenses", {
          expensesItem: item,
          assetComponent: this.assetComponent,
        });
      },
      isFieldWithError(field, value) {
        return !value && this.validationErrors && this.validationErrors.includes(field);
      }
    }
  }
</script>

